import React, { useEffect } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid2,
  IconButton,
  Switch,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { runInAction } from "mobx";
import { observer, useLocalObservable } from "mobx-react";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { CategoryStore } from "../../../stores/CategoryStore";
import {
  CategoryCreateUpdatePayload,
  getApiErrorMessage,
  normalizeCategoryCode,
  validateCategoryPayload,
} from "../../../../../models/Categories/types";

export interface CategoryFormModalProps {
  open: boolean;
  store: CategoryStore;
  onClose: () => void;
  onSuccess: (categoryId?: number) => void;
}

const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
  open,
  store,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));
  const { userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const canCreate = userStore.hasPermission(EmployeeRoles.EDIT_CATALOG, true);
  const colors = theme.palette.customColors;

  const form = useLocalObservable(() => ({
    saving: false,
    name: "",
    code: "",
    priority: "0",
    display: 1,
    active: 1,
    has_expiry: 0,
    seoTitle: "",
    seoDescription: "",
    selectedImageUrl: "",
    unselectedImageUrl: "",
    resetForm() {
      this.name = "";
      this.code = "";
      this.priority = "0";
      this.display = 1;
      this.active = 1;
      this.has_expiry = 0;
      this.seoTitle = "";
      this.seoDescription = "";
      this.selectedImageUrl = "";
      this.unselectedImageUrl = "";
    },
  }));

  useEffect(() => {
    if (!open) return;
    runInAction(() => form.resetForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (form.saving) return;
    form.resetForm();
    onClose();
  };

  const buildPayload = (): CategoryCreateUpdatePayload => ({
    name: form.name.trim(),
    code: normalizeCategoryCode(form.code),
    priority: (() => {
      const n = parseInt(form.priority, 10);
      return Number.isNaN(n) ? 0 : n;
    })(),
    display: form.display,
    active: form.active,
    has_expiry: form.has_expiry,
    seo_title: form.seoTitle.trim() || null,
    seo_description: form.seoDescription.trim() || null,
    selected_image_url: form.selectedImageUrl.trim() || null,
    unselected_image_url: form.unselectedImageUrl.trim() || null,
  });

  const handleSubmit = async () => {
    if (!canCreate) {
      showError(
        "Permission denied",
        "You don't have permission to create categories.",
      );
      return;
    }
    const payload = buildPayload();
    const err = validateCategoryPayload(payload);
    if (err) {
      showError("Validation", err);
      return;
    }

    try {
      form.saving = true;
      const created = await store.createCategoryRecord(payload);
      showSuccess("Category created.");
      form.resetForm();
      onClose();
      onSuccess(created.id);
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Could not create category."),
      );
    } finally {
      form.saving = false;
    }
  };

  const flagRow = (
    label: string,
    checked: boolean,
    onToggle: (next: boolean) => void,
  ) => (
    <Box display="flex" alignItems="center" gap={1}>
      <Switch
        checked={checked}
        size="small"
        onChange={(_, next) => onToggle(next)}
        disabled={form.saving}
      />
      <CustomTypography variant="body2">{label}</CustomTypography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown")
          handleClose();
      }}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreenDialog}
      scroll="paper"
      slotProps={{
        paper: {
          sx: {
            borderRadius: { xs: 0, sm: 2.5 },
            overflow: "hidden",
            maxHeight: fullScreenDialog ? "100%" : "92vh",
            display: "flex",
            flexDirection: "column",
            m: { xs: 0, sm: 2 },
            border: { xs: "none", sm: 1 },
            borderColor: "divider",
            boxShadow: (t) =>
              fullScreenDialog
                ? "none"
                : `0 12px 40px ${alpha(t.palette.common.black, 0.08)}`,
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: 3,
          py: 2.5,
          flexShrink: 0,
          gap: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <CustomTypography variant="heading5">Add category</CustomTypography>
          <CustomTypography
            variant="body3"
            color={colors?.greyTextColor}
            sx={{ mt: 0.5 }}
          >
            Create the category first, then map catalog options on the detail
            page.
          </CustomTypography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          size="small"
          disabled={form.saving}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
        <Grid2 container spacing={2} columns={12}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Name *
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="e.g. Apparel"
              value={form.name}
              disabled={form.saving}
              onChange={(e) => {
                form.name = e.target.value || "";
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Product code *
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="POL"
              value={form.code}
              disabled={form.saving}
              onChange={(e) => {
                form.code = (e.target.value || "").toUpperCase();
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Priority
            </CustomTypography>
            <CustomTextField
              width="100%"
              type="number"
              allowNegativeValue={false}
              allowDecimalValue={false}
              placeholder="0"
              value={form.priority}
              disabled={form.saving}
              onChange={(e) => {
                form.priority = e.target.value || "0";
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            {flagRow("Active", form.active === 1, (next) => {
              form.active = next ? 1 : 0;
            })}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            {flagRow("Display", form.display === 1, (next) => {
              form.display = next ? 1 : 0;
            })}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            {flagRow("Has expiry", form.has_expiry === 1, (next) => {
              form.has_expiry = next ? 1 : 0;
            })}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Selected image URL
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="https://"
              value={form.selectedImageUrl}
              disabled={form.saving}
              onChange={(e) => {
                form.selectedImageUrl = e.target.value || "";
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Unselected image URL
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="https://"
              value={form.unselectedImageUrl}
              disabled={form.saving}
              onChange={(e) => {
                form.unselectedImageUrl = e.target.value || "";
              }}
            />
          </Grid2>
          <Grid2 size={12}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              SEO title
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="Title for search snippets"
              value={form.seoTitle}
              disabled={form.saving}
              onChange={(e) => {
                form.seoTitle = e.target.value || "";
              }}
            />
          </Grid2>
          <Grid2 size={12}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              SEO description
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="Short summary for search engines"
              value={form.seoDescription}
              disabled={form.saving}
              onChange={(e) => {
                form.seoDescription = e.target.value || "";
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "flex-end",
          gap: 1.5,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <AppButton
          variant="transparentPrimary"
          label="Cancel"
          onClick={handleClose}
          disabled={form.saving}
        />
        <AppButton
          variant="primary"
          label={form.saving ? "Saving…" : "Create"}
          onClick={() => void handleSubmit()}
          disabled={form.saving || !canCreate}
        />
      </DialogActions>
    </Dialog>
  );
};

export default observer(CategoryFormModal);
